import { getConfig } from "./config";

/**
 * The legal texts are published on the marketing site (nodus.se / nodus.foo / nodus.nu); the
 * portal never carries its own copy. The site domain is the apex of the environment's API host
 * (api.nodus.foo → nodus.foo), taken from getConfig().API_BASE_URL. That covers every deployment:
 * on nodus.* hosts it is derived from the hostname, on customer domains (member.l26.se) from the
 * build's VITE_BASE_URL_DOMAIN, and in the on-site Docker image from the runtime API_BASE_URL.
 * The customer's own domain is never used.
 */
export type LegalDoc = "privacy" | "terms";
export type LegalLang = "sv" | "en";

const PATHS: Record<LegalDoc, Record<LegalLang, string>> = {
  privacy: { sv: "/integritet", en: "/en/privacy" },
  // The member portal terms of use, not the customer (operator) terms.
  terms: { sv: "/anvandarvillkor", en: "/en/member-terms" },
};

/** Production is the fallback, as for the API itself (config.ts). */
const DEFAULT_SITE_DOMAIN = "nodus.se";

export function siteDomainFromApiUrl(apiBaseUrl: string | undefined): string {
  if (!apiBaseUrl) return DEFAULT_SITE_DOMAIN;
  let host: string;
  try {
    host = new URL(apiBaseUrl.includes("://") ? apiBaseUrl : `https://${apiBaseUrl}`).hostname;
  } catch {
    return DEFAULT_SITE_DOMAIN;
  }
  const m = host.match(/(?:^|\.)(nodus\.[a-z]+)$/);
  return m ? m[1] : DEFAULT_SITE_DOMAIN;
}

/** The portal UI has no language setting, so the browser's preference picks sv or en. */
export function legalLang(languages: readonly string[] | undefined): LegalLang {
  const first = languages?.find(Boolean)?.toLowerCase() ?? "";
  return first.startsWith("sv") ? "sv" : "en";
}

export function legalUrl(doc: LegalDoc, lang: LegalLang, siteDomain: string): string {
  return `https://${siteDomain}${PATHS[doc][lang]}`;
}

/** Both language versions of a document for this environment, and the one to redirect to. */
export function legalTargets(doc: LegalDoc): { target: string; sv: string; en: string } {
  const domain = siteDomainFromApiUrl(getConfig().API_BASE_URL);
  const nav = typeof navigator === "undefined" ? undefined : navigator;
  const lang = legalLang(nav?.languages?.length ? nav.languages : nav?.language ? [nav.language] : undefined);
  const sv = legalUrl(doc, "sv", domain);
  const en = legalUrl(doc, "en", domain);
  return { target: lang === "sv" ? sv : en, sv, en };
}
